import Axios from 'axios';
import { toast } from 'sonner';
import i18n, { t } from '@/i18n';
import { resolveApiErrorKey } from '@/i18n/api-error';

export type SuccessErrorResponse<T extends object> = {
	data: T | null;
	error: ErrorResponse | null;
	errorMessage: string | null;
};
export type CommonResponse<T extends object> = T | ErrorResponse;

export type ErrorResponse = {
	error: string;
	message?: string | string[];
	statusCode?: number;
	code?: string;
	errorType?: 'business' | 'system' | 'external_service';
	requestId?: string;
	timestamp?: string;
	path?: string;
};

export function isErrorResponse<T extends object>(response: CommonResponse<T>): response is ErrorResponse {
	return 'error' in response && response.error !== undefined;
}

export function isSuccessResponse<T extends object>(response: CommonResponse<T>): response is T {
	return !isErrorResponse(response);
}

export function getResponseAndErrorMessage<T extends object>(
	response: CommonResponse<T>,
): { data: T | null; errorMessage: string | null; error: ErrorResponse | null } {
	return { data: getResponse(response), errorMessage: getErrorMessage(response), error: getError(response) };
}

export function getResponse<T extends object>(response: CommonResponse<T> | undefined): T | null {
	if (!response) {
		return null;
	}
	if (isSuccessResponse(response)) {
		return response;
	}
	return null;
}
// The backend exception filter can nest the original error body inside `message`
// (e.g. { statusCode, message: { message, error, statusCode }, timestamp }), so a
// runtime `message` may be a string, an array, or an object despite the typings.
// Rendering a non-string (e.g. via toast) crashes React, so always flatten to a string.
function normalizeErrorMessage(message: unknown): string | null {
	if (typeof message === 'string') {
		return message;
	}
	if (Array.isArray(message)) {
		const parts = message.map(normalizeErrorMessage).filter(Boolean);
		return parts.length > 0 ? parts.join(', ') : null;
	}
	if (message && typeof message === 'object') {
		const nested = message as { message?: unknown; error?: unknown };
		return (
			normalizeErrorMessage(nested.message) ??
			(typeof nested.error === 'string' ? nested.error : null)
		);
	}
	return null;
}

export function getErrorMessage<T extends object>(response: CommonResponse<T>): string | null {
	if (isErrorResponse(response)) {
		return normalizeErrorMessage(response.message) || response.error;
	}
	return null;
}

export function getError<T extends object>(response: CommonResponse<T>): ErrorResponse | null {
	if (isErrorResponse(response)) {
		return response;
	}
	return null;
}

export function extractApiError(error: unknown): {
	message: string;
	statusCode: number;
	requestId?: string;
	code?: string;
} {
	const axiosError = error as
		| { response?: { data?: ErrorResponse; status?: number }; request?: unknown; code?: string }
		| undefined;

	const response = axiosError?.response;
	if (response) {
		const data = response.data;
		if (data && (data.message || data.error)) {
			const message =
				normalizeErrorMessage(data.message) ||
				(typeof data.error === 'string' ? data.error : null) ||
				t('common.errors.generic');
			return { message, statusCode: response.status ?? 500, requestId: data.requestId, code: data.code };
		}
		// HTTP error with an empty or non-JSON body (e.g. a proxy 502/504 HTML page).
		// Preserve the real status instead of masking it as 500.
		return { message: t('common.errors.generic'), statusCode: response.status ?? 500 };
	}

	if (axiosError?.code === 'ECONNABORTED' || axiosError?.code === 'ERR_NETWORK') {
		return { message: t('common.errors.networkTimeout'), statusCode: 0 };
	}

	if (axiosError?.request) {
		return { message: t('common.errors.noResponse'), statusCode: 0 };
	}

	// A non-Axios thrown Error (e.g. a client-side guard) — surface its message rather than a generic one.
	if (error instanceof Error && error.message) {
		return { message: error.message, statusCode: 0 };
	}

	return { message: t('common.errors.unexpected'), statusCode: 500 };
}

/** Translated text for a coded backend error, otherwise the raw message. Use this for every error toast. */
export function apiErrorMessage(error: unknown): string {
	const { message, code } = extractApiError(error);
	const key = resolveApiErrorKey(code, (k) => i18n.exists(k));
	return key ? t(key, { defaultValue: message }) : message;
}

/**
 * Error toast for a failed request, for use in mutation `onError` handlers.
 * A cancelled request is not a failure the user needs to hear about: it is
 * either one they aborted themselves or one the axios interceptor dropped
 * while redirecting to login (see `CanceledError` in lib/utils/axios.ts).
 */
export function toastApiError(error: unknown): void {
	if (Axios.isCancel(error)) return;
	toast.error(apiErrorMessage(error));
}
